/**
 * Global Meeting List Component
 * Light QA Contract Compliant:
 * - Semantic tokens for colors
 * - Skeleton loaders for LCP < 2.5s
 * - Accessible table structure
 * - Mobile-first card view
 */

import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Calendar, Clock, MapPin, Building2, Star, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FavoriteButton } from '@/components/meeting/FavoriteButton';
import type { GlobalMeeting } from '@/hooks/global/useGlobalMeetings';
import { MEETING_STATUS_LABELS, MEETING_STATUS_COLORS, type MeetingStatus } from '@/types/meeting';
import { cn } from '@/lib/utils';

interface GlobalMeetingListProps {
  meetings: GlobalMeeting[];
  isLoading?: boolean;
}

const MeetingStatusBadge = ({ status }: { status: MeetingStatus }) => {
  const label = MEETING_STATUS_LABELS[status] || status;
  const colorClass = MEETING_STATUS_COLORS[status] || 'bg-muted text-muted-foreground';

  return (
    <Badge variant="outline" className={`${colorClass} border-0`}>
      {label}
    </Badge>
  );
};

const SkeletonRow = () => (
  <TableRow>
    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
    <TableCell><Skeleton className="h-4 w-48" /></TableCell>
    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
  </TableRow>
);

const MobileSkeletonCard = () => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <Skeleton className="h-8 w-8 rounded" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <div className="flex gap-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Mobile Card Component for responsive display
const MobileMeetingCard = ({ 
  meeting, 
  onClick 
}: { 
  meeting: GlobalMeeting; 
  onClick: () => void;
}) => (
  <Card 
    className="cursor-pointer hover:bg-muted/50 transition-colors touch-manipulation"
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <FavoriteButton meetingId={meeting.id} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{meeting.title}</div>
          <div className="text-sm text-primary truncate">{meeting.project_name}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {format(new Date(meeting.meeting_date), 'dd/MM/yyyy', { locale: vi })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {meeting.start_time?.slice(0, 5) || '-'}
            </span>
            {meeting.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span className="truncate max-w-[120px]">{meeting.location}</span>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <MeetingStatusBadge status={meeting.status} />
            {meeting.project_status && (
              <Badge variant="outline" className="text-xs capitalize">
                {meeting.project_status}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const GlobalMeetingList = ({ meetings, isLoading }: GlobalMeetingListProps) => {
  const navigate = useNavigate();

  const handleRowClick = (meeting: GlobalMeeting) => {
    navigate(`/projects/${meeting.project_id}?tab=meeting&meetingId=${meeting.id}`);
  };

  if (isLoading) {
    return (
      <>
        {/* Desktop skeleton */}
        <div className="hidden md:block rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Dự án</TableHead>
                <TableHead>Tiêu đề</TableHead>
                <TableHead className="w-[120px]">Ngày</TableHead>
                <TableHead className="w-[100px]">Giờ</TableHead>
                <TableHead className="w-[150px]">Địa điểm</TableHead>
                <TableHead className="w-[80px]">Ưu tiên</TableHead>
                <TableHead className="w-[80px]">TT DA</TableHead>
                <TableHead className="w-[100px]">Trạng thái</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Mobile skeleton */}
        <div className="md:hidden space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <MobileSkeletonCard key={i} />
          ))}
        </div>
      </>
    );
  }

  if (!meetings || meetings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground">Chưa có cuộc họp</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Các cuộc họp từ các dự án bạn có quyền truy cập sẽ hiển thị ở đây.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop: Table view */}
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px]">
                <div className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  Dự án
                </div>
              </TableHead>
              <TableHead>Tiêu đề</TableHead>
              <TableHead className="w-[120px]">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Ngày
                </div>
              </TableHead>
              <TableHead className="w-[100px]">
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Giờ
                </div>
              </TableHead>
              <TableHead className="w-[150px]">
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  Địa điểm
                </div>
              </TableHead>
              <TableHead className="w-[50px]">
                <div className="flex items-center justify-center">
                  <Star className="h-4 w-4" />
                </div>
              </TableHead>
              <TableHead className="w-[80px]">
                <div className="flex items-center gap-1">
                  <Briefcase className="h-4 w-4" />
                  TT DA
                </div>
              </TableHead>
              <TableHead className="w-[100px]">Trạng thái</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {meetings.map((meeting) => (
              <TableRow
                key={meeting.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleRowClick(meeting)}
              >
                <TableCell className="font-medium">
                  <span className="truncate max-w-[140px] block" title={meeting.project_name}>
                    {meeting.project_name}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="truncate max-w-[300px] block" title={meeting.title}>
                    {meeting.title}
                  </span>
                </TableCell>
                <TableCell>
                  {format(new Date(meeting.meeting_date), 'dd/MM/yyyy', { locale: vi })}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {meeting.start_time ? meeting.start_time.slice(0, 5) : '-'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  <span className="truncate max-w-[140px] block" title={meeting.location || '-'}>
                    {meeting.location || '-'}
                  </span>
                </TableCell>
                <TableCell>
                  <FavoriteButton meetingId={meeting.id} size="sm" />
                </TableCell>
                <TableCell>
                  {meeting.project_status ? (
                    <Badge variant="outline" className="capitalize text-xs">
                      {meeting.project_status}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <MeetingStatusBadge status={meeting.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile: Card view */}
      <div className="md:hidden space-y-3">
        {meetings.map((meeting) => (
          <MobileMeetingCard 
            key={meeting.id} 
            meeting={meeting} 
            onClick={() => handleRowClick(meeting)} 
          />
        ))}
      </div>
    </>
  );
};

export default GlobalMeetingList;
